import React, { useEffect, useState } from "react";
import axios from "axios";
import { Avatar, AvatarProps } from "@mui/material";
import { useSelector } from "react-redux";
import { API_URL } from "src/config/api";
import { RootState } from "src/store";

interface TalentAvatarProps extends Omit<AvatarProps, "src"> {
  profileId: string;
  avatarId?: string;
  fallbackText?: string;
  blurred?: boolean;
}

const avatarUrlCache = new Map<string, string>();
const avatarRequestCache = new Map<string, Promise<string>>();

const getCacheKey = (profileId: string, avatarId?: string) =>
  `${profileId}:${avatarId || ""}`;

const fetchAvatarUrl = async (
  profileId: string,
  token: string,
  cacheKey: string
) => {
  const cachedUrl = avatarUrlCache.get(cacheKey);
  if (cachedUrl) {
    return cachedUrl;
  }

  const inFlightRequest = avatarRequestCache.get(cacheKey);
  if (inFlightRequest) {
    return inFlightRequest;
  }

  const request = axios
    .post(
      `${API_URL}/talent/profile/getprofileavatar`,
      { profileId },
      {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      }
    )
    .then((response) => {
      const objectUrl = URL.createObjectURL(response.data);
      avatarUrlCache.set(cacheKey, objectUrl);
      return objectUrl;
    })
    .finally(() => {
      avatarRequestCache.delete(cacheKey);
    });

  avatarRequestCache.set(cacheKey, request);
  return request;
};

const TalentAvatar: React.FC<TalentAvatarProps> = ({
  profileId,
  avatarId,
  fallbackText = "",
  blurred = false,
  children,
  sx,
  ...avatarProps
}) => {
  const token = useSelector((state: RootState) => state.auth.token);
  const [src, setSrc] = useState<string>("");

  useEffect(() => {
    let isActive = true;

    if (!profileId || !token) {
      setSrc("");
      return () => {
        isActive = false;
      };
    }

    const cacheKey = getCacheKey(profileId, avatarId);
    const cachedUrl = avatarUrlCache.get(cacheKey);
    if (cachedUrl) {
      setSrc(cachedUrl);
      return () => {
        isActive = false;
      };
    }

    setSrc("");

    fetchAvatarUrl(profileId, token, cacheKey)
      .then((objectUrl) => {
        if (isActive) {
          setSrc(objectUrl);
        }
      })
      .catch(() => {
        if (isActive) {
          setSrc("");
        }
      });

    return () => {
      isActive = false;
    };
  }, [avatarId, profileId, token]);

  return (
    <Avatar
      src={src || undefined}
      sx={[
        {
          ...(blurred
            ? {
                filter: "blur(10px)",
                transform: "scale(1.02)",
                "& img": {
                  filter: "blur(12px)",
                  transform: "scale(1.08)",
                },
              }
            : {}),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...avatarProps}
    >
      {src ? undefined : children || fallbackText}
    </Avatar>
  );
};

export default TalentAvatar;
